import { Duration } from "luxon";
import { useMemo, useSyncExternalStore } from "react";

/**
 * A tick count shared by its subscribers, in the shape `useSyncExternalStore` reads.
 */
export type Ticker = {
  /** Adds a listener called on every tick; returns the function that removes it. */
  subscribe: (listener: () => void) => () => void;
  /** How many ticks have fired. */
  getSnapshot: () => number;
};

/**
 * Counts ticks of one shared timer, which runs only while something subscribes.
 */
export function createTicker(interval: Duration): Ticker {
  let ticks = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    // The first subscriber starts the timer.
    if (timer === undefined) {
      timer = setInterval(() => {
        ticks += 1;
        for (const notify of listeners) notify();
      }, interval.toMillis());
    }
    return () => {
      listeners.delete(listener);
      // The last one to leave stops it.
      if (listeners.size > 0) return;
      clearInterval(timer);
      timer = undefined;
    };
  };

  return { subscribe, getSnapshot: () => ticks };
}

const minuteTicker = createTicker(Duration.fromObject({ minutes: 1 }));

/**
 * A clock that ticks once a minute, so relative times age together without re-rendering per second.
 *
 * Every caller shares one timer, so a component rendered once per row may call it directly. A caller reads the time
 * when it mounts and again on each shared tick.
 */
export function useMinuteClock(): number {
  const ticks = useSyncExternalStore(minuteTicker.subscribe, minuteTicker.getSnapshot, minuteTicker.getSnapshot);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a tick is the trigger, not an input
  return useMemo(() => Date.now(), [ticks]);
}
